import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import {
  listClassrooms,
  updateClassroom,
  deleteClassroom,
  allocateTeacher,
  getNominalListPdf,
} from "../services/classroom";
import { levelKey } from "../utils/classesLabels";
import { listTeachers, listFrequencies } from "../services/teacher";
import MultiSelectField from "./shared/MultiSelectField";
import { useConfirm } from "../hook/useConfirm";

const PAGE_SIZE = 8;
const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i);
const SHIFT_OPTIONS = ["Matutino", "Vespertino", "Diurno", "Nocturno"];

// Same UX pattern as ListaEstudantesLayer (header stat cards, filters bar,
// bordered/expandable table, portal-based "Opções" kebab menu) applied to
// Turmas — mirroring sge_infantil_web's src/pages/class/view/index.js, which
// is the reference for the filters (nome/ano/nível/turno) and for what the
// row expansion shows (the turma's allocated student roster). The "Imprimir
// Lista Nominal" action from that same reference is kept as a menu item and
// duplicated as a shortcut inside the expanded roster tab.
const ListaTurmasLayer = () => {
  const { t } = useTranslation();
  const { confirm, ConfirmModal } = useConfirm();
  const navigate = useNavigate();

  const [filterYear, setFilterYear] = useState(currentYear);
  const [classrooms, setClassrooms] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [className, setClassName] = useState("");
  const [level, setLevel] = useState("");
  const [shift, setShift] = useState("");
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editTeacherIds, setEditTeacherIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const [printingId, setPrintingId] = useState(null);

  // Same portal-based menu approach as ListaEstudantesLayer — rendered into
  // document.body and positioned with `position: fixed` so it isn't clipped
  // by the `.table-responsive` scroll container.
  const [openMenu, setOpenMenu] = useState(null); // { id, top, left, record }
  const menuRef = useRef(null);

  const fetchClassrooms = () => {
    setLoading(true);
    listClassrooms(filterYear)
      .then((resp) => setClassrooms(resp.data || []))
      .catch(() => toast.error(t("turma.error.load")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchClassrooms();
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterYear]);

  useEffect(() => {
    listFrequencies().then((resp) => setFrequencies(resp.data || [])).catch(() => setFrequencies([]));
    listTeachers().then((resp) => setTeachers(resp.data || [])).catch(() => setTeachers([]));
  }, []);

  const frequencyName = (id) => frequencies.find((f) => f.id === id)?.description || "-";

  const genderLabel = (value) => {
    const normalized = (value || "").toString().trim().toUpperCase();
    if (normalized.startsWith("M")) return t("global.male");
    if (normalized.startsWith("F")) return t("global.female");
    return "-";
  };

  const filtered = useMemo(() => {
    const term = className.trim().toLowerCase();
    return classrooms.filter((c) => {
      const matchesName = !term || c.className?.toLowerCase().includes(term);
      const matchesLevel = !level || String(c.educationLevel) === String(level);
      const matchesShift = !shift || c.shift === shift;
      return matchesName && matchesLevel && matchesShift;
    });
  }, [classrooms, className, level, shift]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const goToPage = (next) => {
    if (next < 1 || next > totalPages) return;
    setPage(next);
  };

  const clearFilters = () => {
    setClassName("");
    setLevel("");
    setShift("");
    setPage(1);
  };

  const genderCounts = useMemo(() => {
    let male = 0;
    let female = 0;
    filtered.forEach((c) => {
      (c.students || []).forEach((s) => {
        const normalized = (s.gender || "").toString().trim().toUpperCase();
        if (normalized.startsWith("M")) male += 1;
        else if (normalized.startsWith("F")) female += 1;
      });
    });
    return { male, female };
  }, [filtered]);

  const toggleExpand = (id) => setExpandedId((cur) => (cur === id ? null : id));

  const MENU_WIDTH = 240;

  const toggleMenu = (e, record) => {
    e.stopPropagation();
    if (openMenu?.id === record.id) {
      setOpenMenu(null);
      return;
    }
    // Read currentTarget synchronously — React nulls it out once the event
    // finishes dispatching, and in StrictMode the setOpenMenu updater above
    // used to be re-invoked later (to detect impure reducers), by which
    // point `e.currentTarget` was already null.
    const rect = e.currentTarget.getBoundingClientRect();
    const left = Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));
    setOpenMenu({ id: record.id, top: rect.bottom + 6, left, record });
  };

  const closeMenu = () => setOpenMenu(null);

  useEffect(() => {
    if (!openMenu) return undefined;
    const handlePointerDown = (e) => {
      if (e.target.closest("[data-turma-actions]")) return;
      closeMenu();
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("scroll", closeMenu, true);
    window.addEventListener("resize", closeMenu);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("scroll", closeMenu, true);
      window.removeEventListener("resize", closeMenu);
    };
  }, [openMenu]);

  const openEdit = (classroom) => {
    setEditTarget(classroom);
    setEditForm({
      className: classroom.className || "",
      code: classroom.code || "",
      academicYear: classroom.academicYear || currentYear,
      educationLevel: classroom.educationLevel || "",
      shift: classroom.shift || "Diurno",
      maxStudents: classroom.maxStudents || 1,
    });
    setEditTeacherIds((classroom.teachers || []).map((tc) => tc.id));
  };

  const closeEdit = () => {
    setEditTarget(null);
    setEditForm(null);
    setEditTeacherIds([]);
  };

  const handleSaveEdit = async () => {
    if (!editTarget || !editForm) return;
    setSaving(true);
    try {
      await updateClassroom(editTarget.id, {
        ...editForm,
        academicYear: Number(editForm.academicYear),
        maxStudents: Number(editForm.maxStudents) || 1,
      });
      const selectedTeachers = teachers
        .filter((tc) => editTeacherIds.includes(tc.id))
        .map((tc) => ({ id: tc.id, name: tc.name }));
      await allocateTeacher(editTarget.id, selectedTeachers);
      toast.success(t("turma.success.updated"));
      closeEdit();
      fetchClassrooms();
    } catch (err) {
      toast.error(t("turma.error.update"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (classroom) => {
    if (!(await confirm(t("turma.confirm.delete", { name: classroom.className })))) return;
    try {
      await deleteClassroom(classroom.id);
      toast.success(t("turma.success.deleted"));
      fetchClassrooms();
    } catch (err) {
      toast.error(t("turma.error.delete"));
    }
  };

  // Allocation moved out of a modal into its own page (dual-list transfer UI,
  // mirroring sge_infantil_web's class/allocateStudent screen) since picking
  // students works better with real screen space than a modal — the
  // classroom object travels via router state so the page doesn't need a
  // (non-existent) "get classroom by id" endpoint.
  const goToAllocate = (classroom) => {
    navigate(`/turmas/alocar-estudantes/${classroom.id}`, { state: { classroom } });
  };

  const handlePrintNominalList = async (classroom) => {
    setPrintingId(classroom.id);
    try {
      const resp = await getNominalListPdf(classroom.id);
      navigate("/documentos/pdf", {
        state: {
          url: resp.data.url,
          title: t("turma.documento.nominalList", { className: classroom.className }),
          filename: `lista-nominal-${classroom.className}.pdf`,
        },
      });
    } catch (err) {
      toast.error(t("turma.error.print"));
    } finally {
      setPrintingId(null);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />
      {ConfirmModal}

      <div className='col-12'>
        <div className='d-flex flex-wrap align-items-center gap-3'>
          <div className='card flex-fill radius-12 border-0 shadow-sm'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='mdi:google-classroom' />
              </span>
              <div>
                <h6 className='mb-0'>{filtered.length}</h6>
                <span className='text-secondary-light text-sm'>{t("turma.list.stat.total")}</span>
              </div>
            </div>
          </div>
          <div className='card flex-fill radius-12 border-0 shadow-sm'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-lilac-50 text-lilac-600 d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='mdi:gender-male' />
              </span>
              <div>
                <h6 className='mb-0'>{genderCounts.male}</h6>
                <span className='text-secondary-light text-sm'>{t("global.male")}</span>
              </div>
            </div>
          </div>
          <div className='card flex-fill radius-12 border-0 shadow-sm'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-success-focus text-success-main d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='mdi:gender-female' />
              </span>
              <div>
                <h6 className='mb-0'>{genderCounts.female}</h6>
                <span className='text-secondary-light text-sm'>{t("global.female")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24'>
              <div>
                <h6 className='mb-4'>{t("turma.breadcrumb.list")}</h6>
                <p className='text-secondary-light mb-0 text-sm'>{t("turma.list.subtitle")}</p>
              </div>
              <button
                type='button'
                className='btn btn-primary-600 d-flex align-items-center gap-2'
                onClick={() => navigate("/turmas/criar")}
              >
                <Icon icon='ic:baseline-plus' />
                {t("turma.action.create")}
              </button>
            </div>

            <div className='p-16 radius-8 bg-neutral-50 mb-24'>
              <div className='row gy-3 align-items-end'>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("turma.field.academicYear")}</label>
                  <select
                    className='form-control form-select'
                    value={filterYear}
                    onChange={(e) => setFilterYear(Number(e.target.value))}
                  >
                    {YEAR_OPTIONS.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label text-sm mb-8'>{t("turma.field.className")}</label>
                  <div className='icon-field'>
                    <span className='icon top-50 translate-middle-y'>
                      <Icon icon='ion:search-outline' />
                    </span>
                    <input
                      type='text'
                      className='form-control'
                      placeholder={t("turma.list.search.placeholder")}
                      value={className}
                      onChange={(e) => {
                        setClassName(e.target.value);
                        setPage(1);
                      }}
                    />
                  </div>
                </div>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label text-sm mb-8'>{t(levelKey("turma.field.educationLevel"))}</label>
                  <select
                    className='form-control form-select'
                    value={level}
                    onChange={(e) => {
                      setLevel(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value=''>{t("global.all")}</option>
                    {frequencies.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label text-sm mb-8'>{t("turma.field.shift")}</label>
                  <select
                    className='form-control form-select'
                    value={shift}
                    onChange={(e) => {
                      setShift(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value=''>{t("global.all")}</option>
                    {SHIFT_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-lg-1'>
                  <button
                    type='button'
                    className='btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center'
                    title={t("global.clean.fields")}
                    onClick={clearFilters}
                  >
                    <Icon icon='ic:round-refresh' />
                  </button>
                </div>
              </div>
            </div>

            <div className='table-responsive scroll-sm'>
              <table className='table bordered-table mb-0'>
                <thead>
                  <tr>
                    <th scope='col' style={{ width: 36 }} />
                    <th scope='col'>{t("turma.col.className")}</th>
                    <th scope='col'>{t(levelKey("turma.col.educationLevel"))}</th>
                    <th scope='col'>{t("turma.col.shift")}</th>
                    <th scope='col'>{t("turma.col.teachers")}</th>
                    <th scope='col'>{t("turma.col.students")}</th>
                    <th scope='col' className='text-end'>
                      {t("turma.col.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={7} className='text-center text-secondary-light py-32'>
                        {t("global.loading")}
                      </td>
                    </tr>
                  )}
                  {!loading && pageItems.length === 0 && (
                    <tr>
                      <td colSpan={7} className='text-center text-secondary-light py-32'>
                        {t("turma.hint.noClassrooms")}
                      </td>
                    </tr>
                  )}
                  {pageItems.map((classroom) => {
                    const isExpanded = expandedId === classroom.id;
                    const students = classroom.students || [];
                    const capacity = classroom.maxStudents || 0;
                    const full = capacity > 0 && students.length >= capacity;
                    const busy = printingId === classroom.id;
                    return (
                      <React.Fragment key={classroom.id}>
                        <tr
                          role='button'
                          onClick={() => toggleExpand(classroom.id)}
                          className={isExpanded ? "bg-neutral-50" : ""}
                        >
                          <td>
                            <Icon
                              icon={isExpanded ? "ic:round-expand-less" : "ic:round-expand-more"}
                              className='text-secondary-light text-xl'
                            />
                          </td>
                          <td className='fw-semibold text-primary-light'>
                            {classroom.className}
                            {classroom.code && (
                              <span className='text-secondary-light text-sm d-block'>{classroom.code}</span>
                            )}
                          </td>
                          <td>{frequencyName(classroom.educationLevel)}</td>
                          <td>{classroom.shift || "-"}</td>
                          <td>{(classroom.teachers || []).map((tc) => tc.name).join(", ") || "-"}</td>
                          <td>
                            <span className={full ? "text-danger-600 fw-medium" : ""}>
                              {students.length}/{capacity || "-"}
                            </span>
                          </td>
                          <td className='text-end' onClick={(e) => e.stopPropagation()}>
                            <button
                              type='button'
                              data-turma-actions
                              className='w-32-px h-32-px d-inline-flex align-items-center justify-content-center radius-8 border bg-base'
                              aria-expanded={openMenu?.id === classroom.id}
                              disabled={busy}
                              onClick={(e) => toggleMenu(e, classroom)}
                            >
                              <Icon icon='entypo:dots-three-vertical' />
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} className='p-0'>
                              <div className='p-24 bg-neutral-25 border-top'>
                                <h6 className='mb-12'>{t("turma.expand.tab.details")}</h6>
                                <table className='table table-bordered table-sm mb-24 bg-base'>
                                  <tbody>
                                    <tr>
                                      <th className='w-25 bg-neutral-50'>{t("turma.field.className")}</th>
                                      <td>
                                        <strong>{classroom.className}</strong>
                                      </td>
                                      <th className='bg-neutral-50'>{t("turma.field.code")}</th>
                                      <td>{classroom.code || "-"}</td>
                                    </tr>
                                    <tr>
                                      <th className='bg-neutral-50'>{t("turma.field.academicYear")}</th>
                                      <td>{classroom.academicYear || filterYear}</td>
                                      <th className='bg-neutral-50'>{t(levelKey("turma.field.educationLevel"))}</th>
                                      <td>{frequencyName(classroom.educationLevel)}</td>
                                    </tr>
                                    <tr>
                                      <th className='bg-neutral-50'>{t("turma.field.shift")}</th>
                                      <td>{classroom.shift || "-"}</td>
                                      <th className='bg-neutral-50'>{t("turma.field.maxStudents")}</th>
                                      <td>{capacity || "-"}</td>
                                    </tr>
                                    <tr>
                                      <th className='bg-neutral-50'>{t("turma.field.teachers")}</th>
                                      <td>{(classroom.teachers || []).map((tc) => tc.name).join(", ") || "-"}</td>
                                      <th className='bg-neutral-50'>{t("turma.col.students")}</th>
                                      <td className={full ? "text-danger-600 fw-medium" : ""}>
                                        {students.length}/{capacity || "-"}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>

                                <div className='d-flex align-items-center justify-content-between mb-12'>
                                  <h6 className='mb-0'>
                                    {t("turma.expand.tab.students")} ({students.length})
                                  </h6>
                                  <button
                                    type='button'
                                    className='btn btn-outline-success-600 btn-sm d-flex align-items-center gap-2'
                                    onClick={() => handlePrintNominalList(classroom)}
                                    disabled={printingId === classroom.id}
                                  >
                                    <Icon icon='solar:printer-outline' />
                                    {t("turma.action.nominalList")}
                                  </button>
                                </div>
                                {students.length === 0 ? (
                                  <div className='text-center text-secondary-light py-32'>
                                    <Icon
                                      icon='mdi:account-off-outline'
                                      className='text-3xl d-block mx-auto mb-8'
                                    />
                                    {t("turma.expand.empty.students")}
                                  </div>
                                ) : (
                                  <table className='table table-bordered table-sm mb-0 bg-base'>
                                    <thead>
                                      <tr>
                                        <th style={{ width: 48 }}>Nº</th>
                                        <th>{t("turma.field.code")}</th>
                                        <th>{t("global.name")}</th>
                                        <th>{t("global.gender")}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {students.map((s, idx) => (
                                        <tr key={s.id || idx}>
                                          <td>{idx + 1}</td>
                                          <td>{s.code || "-"}</td>
                                          <td>{s.name}</td>
                                          <td>{genderLabel(s.gender)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mt-16'>
              <span className='text-secondary-light text-sm'>
                {filtered.length} {t("turma.list.total")}
              </span>
              {!loading && filtered.length > PAGE_SIZE && (
                <ul className='pagination d-flex flex-wrap align-items-center gap-2 mb-0'>
                  <li className='page-item'>
                    <button
                      type='button'
                      className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                    >
                      <Icon icon='ep:d-arrow-left' className='text-xl' />
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <li key={p} className='page-item'>
                      <button
                        type='button'
                        className={`page-link fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px ${
                          p === page ? "bg-primary-600 text-white" : "bg-primary-50 text-secondary-light"
                        }`}
                        onClick={() => goToPage(p)}
                      >
                        {p}
                      </button>
                    </li>
                  ))}
                  <li className='page-item'>
                    <button
                      type='button'
                      className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                      onClick={() => goToPage(page + 1)}
                      disabled={page === totalPages}
                    >
                      <Icon icon='ep:d-arrow-right' className='text-xl' />
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {editTarget && editForm && (
        <div className='modal show d-block' style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className='modal-dialog modal-lg'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h6 className='mb-0'>{t("turma.action.edit")}</h6>
                <button type='button' className='btn-close' onClick={closeEdit} />
              </div>
              <div className='modal-body'>
                <div className='row gy-3'>
                  <div className='col-sm-6'>
                    <label className='form-label'>{t("turma.field.className")}</label>
                    <input
                      type='text'
                      className='form-control'
                      value={editForm.className}
                      onChange={(e) => setEditForm((f) => ({ ...f, className: e.target.value }))}
                    />
                  </div>
                  <div className='col-sm-3'>
                    <label className='form-label'>{t("turma.field.code")}</label>
                    <input
                      type='text'
                      className='form-control'
                      value={editForm.code}
                      onChange={(e) => setEditForm((f) => ({ ...f, code: e.target.value }))}
                    />
                  </div>
                  <div className='col-sm-3'>
                    <label className='form-label'>{t("turma.field.academicYear")}</label>
                    <select
                      className='form-select'
                      value={editForm.academicYear}
                      onChange={(e) => setEditForm((f) => ({ ...f, academicYear: e.target.value }))}
                    >
                      {YEAR_OPTIONS.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className='col-sm-4'>
                    <label className='form-label'>{t(levelKey("turma.field.educationLevel"))}</label>
                    <select
                      className='form-select'
                      value={editForm.educationLevel}
                      onChange={(e) => setEditForm((f) => ({ ...f, educationLevel: e.target.value }))}
                    >
                      <option value=''>{t(levelKey("turma.field.educationLevel.placeholder"))}</option>
                      {frequencies.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className='col-sm-4'>
                    <label className='form-label'>{t("turma.field.shift")}</label>
                    <select className='form-select' value={editForm.shift} onChange={(e) => setEditForm((f) => ({ ...f, shift: e.target.value }))}>
                      {SHIFT_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className='col-sm-4'>
                    <label className='form-label'>{t("turma.field.maxStudents")}</label>
                    <input
                      type='number'
                      min='1'
                      className='form-control'
                      value={editForm.maxStudents}
                      onChange={(e) => setEditForm((f) => ({ ...f, maxStudents: e.target.value }))}
                    />
                  </div>
                  <div className='col-12'>
                    <label className='form-label'>{t("turma.field.teachers")}</label>
                    <MultiSelectField
                      items={teachers}
                      selectedIds={editTeacherIds}
                      onChange={setEditTeacherIds}
                      placeholder={t("turma.field.teachers.placeholder")}
                      noOptionsMessage={t("turma.hint.noTeachers")}
                    />
                  </div>
                </div>
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-outline-secondary' onClick={closeEdit}>
                  {t("global.cancel")}
                </button>
                <button type='button' className='btn btn-primary-600' onClick={handleSaveEdit} disabled={saving}>
                  {saving ? t("global.loading") : (<><i className='ri-save-line' /> {t("global.save")}</>)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {openMenu &&
        createPortal(
          <ul
            ref={menuRef}
            data-turma-actions
            className='dropdown-menu show p-12 border bg-base shadow'
            style={{ position: "fixed", top: openMenu.top, left: openMenu.left, width: MENU_WIDTH, zIndex: 1080 }}
          >
            <li>
              <button
                type='button'
                className='dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 d-flex align-items-center gap-10'
                onClick={() => {
                  closeMenu();
                  openEdit(openMenu.record);
                }}
              >
                <Icon icon='solar:pen-2-linear' /> {t("global.edit")}
              </button>
            </li>
            <li>
              <button
                type='button'
                className='dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 d-flex align-items-center gap-10'
                onClick={() => {
                  closeMenu();
                  goToAllocate(openMenu.record);
                }}
              >
                <Icon icon='mdi:account-multiple-plus-outline' /> {t("turma.action.allocateStudents")}
              </button>
            </li>
            <li>
              <hr className='dropdown-divider' />
            </li>
            <li>
              <button
                type='button'
                className='dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 d-flex align-items-center gap-10'
                onClick={() => {
                  closeMenu();
                  handlePrintNominalList(openMenu.record);
                }}
              >
                <Icon icon='solar:printer-linear' /> {t("turma.action.nominalList")}
              </button>
            </li>
            <li>
              <hr className='dropdown-divider' />
            </li>
            <li>
              <button
                type='button'
                className='dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-danger-100 text-hover-danger-600 d-flex align-items-center gap-10'
                onClick={() => {
                  closeMenu();
                  handleDelete(openMenu.record);
                }}
              >
                <Icon icon='mingcute:delete-2-line' /> {t("global.delete")}
              </button>
            </li>
          </ul>,
          document.body,
        )}
    </div>
  );
};

export default ListaTurmasLayer;
